import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Modal from '../../../components/modal/modal.component';
import {IonAlert, useIonViewWillLeave} from '@ionic/react';
import {Toast} from "@capacitor/toast";
import moment from "moment";
import {Order} from "../reportPage.component";
import {Moment} from "moment/moment";
import {
    checkCameraPermission,
    openSettings,
    startContinuousScan,
    startBatchContinuousScan,
    makeWebViewHidden,
    restoreWebView,
    toggleTorch,
    BARCODE_1D_FORMATS,
    ContinuousScanHandle,
} from "@services/barcodeScanner.service";
import type { Barcode } from "@services/barcodeScanner.service";
import {ReportSetting} from "@enums/report";
import {isSettingEnabled} from "@services/report.service";

type QrScannerModalProps = {
    isOpen: boolean;
    onClose: () => void;
    order?: Order;
    onSubmit: (label: ReportRowComponentLabel) => void;
    onSingleSubmit: (labelName: string, labelValue: string) => void;
};

export type ReportRowComponentLabel = {
    quantity: string|null;
    componentNumberText: string|null;
    serialNumber: string|null;
    levNumber: string|null;
    deliveryNote: string|null;
    comment: string|null;
    batchNumber: string|null;
    materialNumber: string|null;
    measureResult1: string|null;
    measureResult2: string|null;
    measureResult3: string|null;
    customField1: string|null;
    customField2: string|null;
    customField3: string|null;
    customField4: string|null;
}

const LabelScannerModal: React.FC<QrScannerModalProps> = (props: QrScannerModalProps) => {

    const {t} = useTranslation();
    const [showModal, updateShowModal] = useState<boolean>(false);
    const [showPermissionAlert, setShowPermissionAlert] = useState<boolean>(false);
    const [scanStartAt, setScanStartAt] = useState<Moment|undefined>();
    const [scanIntervalAt, setScanIntervalAt] = useState<Moment|undefined>();
    const [scannedLabel, setScannedLabel] = useState<ReportRowComponentLabel>({
        comment: null,
        quantity: null,
        deliveryNote: null,
        levNumber: null,
        serialNumber: null,
        componentNumberText: null,
        batchNumber: null,
        materialNumber: null,
        measureResult1: null,
        measureResult2: null,
        measureResult3: null,
        customField1: null,
        customField2: null,
        customField3: null,
        customField4: null,
    });

    const scanHandleRef = useRef<ContinuousScanHandle | null>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    // Prefixes already captured in current scan session
    const scannedPrefixesRef = useRef<Set<string>>(new Set());
    // Smoothed cornerPoint positions keyed by rawValue (exponential moving average)
    const smoothedPositionsRef = useRef<Map<string, [number, number][]>>(new Map());

    const LERP = 0.45; // fraction moved toward new position each frame

    // Sort corner points clockwise by angle from centroid.
    const sortCornerPoints = (pts: [number, number][]): [number, number][] => {
        const cx = pts.reduce((s, [x]) => s + x, 0) / pts.length;
        const cy = pts.reduce((s, [, y]) => s + y, 0) / pts.length;
        return [...pts].sort(([ax, ay], [bx, by]) => Math.atan2(ay - cy, ax - cx) - Math.atan2(by - cy, bx - cx));
    };

    // Find the rotation of sorted (clockwise) points that minimises total
    // squared distance to prev. Only rotations are considered, so the polygon
    // always stays convex — no crossing edges, no envelopes or diamonds.
    const bestRotation = (prev: [number, number][], sorted: [number, number][]): [number, number][] => {
        let bestScore = Infinity;
        let best = sorted;
        for (let r = 0; r < sorted.length; r++) {
            const rotated = [...sorted.slice(r), ...sorted.slice(0, r)] as [number, number][];
            const score = rotated.reduce((s, [nx, ny], i) => {
                const dx = nx - prev[i][0];
                const dy = ny - prev[i][1];
                return s + dx * dx + dy * dy;
            }, 0);
            if (score < bestScore) { bestScore = score; best = rotated; }
        }
        return best;
    };

    const drawBarcodeOverlay = (barcodes: Barcode[], scannedPrefixes: Set<string>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Plugin returns cornerPoints in physical pixels (Java: displayMetrics.widthPixels/heightPixels).
        // Canvas is sized in CSS pixels (window.innerWidth/Height).
        // Conversion: CSS px = physical px / devicePixelRatio
        const dpr = window.devicePixelRatio || 1;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        for (const barcode of barcodes) {
            if (!barcode.cornerPoints || barcode.cornerPoints.length < 4) continue;
            const rawValue = barcode.rawValue ?? '';
            const prefix = rawValue.charAt(0);
            const isScanned = scannedPrefixes.has(prefix);

            const sorted = sortCornerPoints(barcode.cornerPoints.map(([x, y]) => [x / dpr, y / dpr] as [number, number]));
            const prev = smoothedPositionsRef.current.get(rawValue);
            const aligned = prev ? bestRotation(prev, sorted) : sorted;
            const points: [number, number][] = prev
                ? aligned.map(([nx, ny], i) => [prev[i][0] + (nx - prev[i][0]) * LERP, prev[i][1] + (ny - prev[i][1]) * LERP])
                : aligned;

            smoothedPositionsRef.current.set(rawValue, points);

            ctx.beginPath();
            ctx.moveTo(points[0][0], points[0][1]);
            for (let i = 1; i < points.length; i++) {
                ctx.lineTo(points[i][0], points[i][1]);
            }
            ctx.closePath();

            ctx.strokeStyle = isScanned ? '#00ff44' : '#ffffff';
            ctx.lineWidth = 3;
            ctx.stroke();

            if (prefix) {
                ctx.font = 'bold 14px Inter, sans-serif';
                ctx.fillStyle = isScanned ? '#00ff44' : '#ffffff';
                ctx.fillText(prefix, points[0][0] + 4, points[0][1] - 6);
            }
        }

        // Remove barcodes that left the frame
        const visible = new Set(barcodes.map(b => b.rawValue));
        smoothedPositionsRef.current.forEach((_, key) => {
            if (!visible.has(key)) smoothedPositionsRef.current.delete(key);
        });
    };

    const handleClose = () => {
        props.onClose();
        stopScan();
    };

    useEffect(() => {
        handleOpen();
    }, [props.isOpen]);

    const handleOpen = () => {
        updateShowModal(props.isOpen);
        if (props.isOpen) {
            setScanStartAt(moment());
            setScanIntervalAt(moment());
            scanLabel();
        }
    };

    const onSubmit = async (label: ReportRowComponentLabel) => {
        props.onSubmit(label);
    };

    const onSingleScan = async (labelName: string, labelValue: string, fieldName: string) => {
        await Toast.show({
            text: labelName+': '+labelValue,
            duration: 'short',
            position: 'bottom',
        });

        props.onSingleSubmit(fieldName, labelValue);
    };

    let scanTimeout: NodeJS.Timeout | undefined;

    useEffect(() => {
        checkScanInterval();
        if (scanTimeout) clearInterval(scanTimeout);

        if (scanStartAt) {
            scanTimeout = setInterval(checkScanInterval, 1000);
        }

        return () => {
            if (scanTimeout) clearInterval(scanTimeout);
        };
    }, [scanIntervalAt])

    const checkScanInterval = () => {
        let now = moment();
        if (scanStartAt && now.diff(scanStartAt, 's') > (props.order?.ep2_scanner_timeout ? parseInt(props.order.ep2_scanner_timeout) : 15)) {
            onSubmit(scannedLabel);
            stopScan();
        }
    }

    useIonViewWillLeave(() => {
        stopScan();
    });

    const startScan = async () => {
        let scanStarted:any = undefined;
        let scanned:string[] = [];
        makeWebViewHidden();
        let label:ReportRowComponentLabel = {
            comment: null,
            quantity: null,
            deliveryNote: null,
            levNumber: null,
            serialNumber: null,
            componentNumberText: null,
            batchNumber: null,
            materialNumber: null,
            measureResult1: null,
            measureResult2: null,
            measureResult3: null,
            customField1: null,
            customField2: null,
            customField3: null,
            customField4: null,
        };

        const handle = await startContinuousScan(BARCODE_1D_FORMATS, (rawValue) => {
            let now = moment();
            if (scanStarted === undefined) {
                scanStarted = now;
                setScanStartAt(now)
            }

            if (rawValue) {
                let labelName = rawValue.charAt(0);
                let labelValue = props.order?.scanning_settings.prefixKeepPrefix ? rawValue : rawValue.substring(1);

                switch (labelName) {
                    case 'N':
                        if (!scanned.includes(labelName)) {
                            scanned.push(labelName);
                            onSingleScan(labelName, labelValue, 'deliveryNote');
                            label.deliveryNote = labelValue;
                        }
                        break;
                    case 'P':
                        if (!scanned.includes(labelName)) {
                            scanned.push(labelName);
                            onSingleScan(labelName, labelValue, 'componentNumberText');
                            label.componentNumberText = labelValue;
                        }
                        break;
                    case 'V':
                        if (!scanned.includes(labelName)) {
                            scanned.push(labelName);
                            onSingleScan(labelName, labelValue, 'levNumber');
                            label.levNumber = labelValue;
                        }
                        break;
                    case 'S':
                    case 'M':
                    case 'X':
                        if (!scanned.includes(labelName)) {
                            scanned.push(labelName);
                            onSingleScan(labelName, labelValue, 'serialNumber');
                            label.serialNumber = labelValue;
                        }
                        break;
                    case 'Q':
                        if (!scanned.includes(labelName)) {
                            scanned.push(labelName);
                            onSingleScan(labelName, labelValue, 'quantity');
                            label.quantity = labelValue;
                        }
                        break;
                    case 'H':
                        if (!scanned.includes(labelName)) {
                            scanned.push(labelName);
                            onSingleScan(labelName, labelValue, 'batchNumber');
                            label.batchNumber = labelValue;
                        }
                        break;
                }
            }

            setScannedLabel(label);
            setScanIntervalAt(moment());

            if (now.diff(scanStarted, 's') > (props.order?.ep2_scanner_timeout ? parseInt(props.order.ep2_scanner_timeout) : 15)) {
                stopScan();
                onSubmit(label);
            }
        }, { enableTorch: props.order?.scanning_settings.torchDefault ?? false });

        scanHandleRef.current = handle;
    };

    // ── Batch scan ─────────────────────────────────────────────────────
    // Collects all codes across frames, waits until all 6 prefix types
    // are found OR 3s pass with no new code, then submits everything at once.
    const startBatchScan = async () => {
        let scanStarted: any = undefined;
        let scanned: string[] = [];
        let toastBuffer: string[] = [];
        let toastTimeout: NodeJS.Timeout | null = null;
        scannedPrefixesRef.current = new Set();
        smoothedPositionsRef.current = new Map();
        makeWebViewHidden();
        let label: ReportRowComponentLabel = {
            comment: null,
            quantity: null,
            deliveryNote: null,
            levNumber: null,
            serialNumber: null,
            componentNumberText: null,
            batchNumber: null,
            materialNumber: null,
            measureResult1: null,
            measureResult2: null,
            measureResult3: null,
            customField1: null,
            customField2: null,
            customField3: null,
            customField4: null,
        };

        // Build expected prefixes based on order settings
        const expectedPrefixes: string[] = ['P', 'Q']; // always expected
        if (props.order && isSettingEnabled(ReportSetting.DELIVERY_NOTE, props.order)) expectedPrefixes.push('N');
        if (props.order && isSettingEnabled(ReportSetting.LEV_NUMBER, props.order)) expectedPrefixes.push('V');
        if (props.order && isSettingEnabled(ReportSetting.SERIAL_NUMBER, props.order)) expectedPrefixes.push('S');
        if (props.order && isSettingEnabled(ReportSetting.BATCH_NUMBER, props.order)) expectedPrefixes.push('H');

        const flushToast = () => {
            if (toastBuffer.length > 0) {
                Toast.show({ text: toastBuffer.join(', '), duration: 'short', position: 'bottom' });
                toastBuffer = [];
            }
            toastTimeout = null;
        };

        const finishBatch = () => {
            if (toastTimeout) clearTimeout(toastTimeout);
            flushToast();
            stopScan();
            onSubmit(label);
        };

        const handle = await startBatchContinuousScan(BARCODE_1D_FORMATS, (frameBarcodes) => {
            let now = moment();
            if (scanStarted === undefined) {
                scanStarted = now;
                setScanStartAt(now);
            }

            // Draw live overlay for all barcodes visible in this frame
            drawBarcodeOverlay(frameBarcodes, scannedPrefixesRef.current);

            let newInFrame: string[] = [];

            for (const barcode of frameBarcodes) {
                const rawValue = barcode.rawValue;
                if (!rawValue) continue;
                let labelName = rawValue.charAt(0);
                let labelValue = props.order?.scanning_settings.prefixKeepPrefix ? rawValue : rawValue.substring(1);

                if (scanned.includes(labelName)) continue;

                switch (labelName) {
                    case 'N':
                        scanned.push(labelName);
                        scannedPrefixesRef.current.add(labelName);
                        label.deliveryNote = labelValue;
                        newInFrame.push(labelName + ': ' + labelValue);
                        break;
                    case 'P':
                        scanned.push(labelName);
                        scannedPrefixesRef.current.add(labelName);
                        label.componentNumberText = labelValue;
                        newInFrame.push(labelName + ': ' + labelValue);
                        break;
                    case 'V':
                        scanned.push(labelName);
                        scannedPrefixesRef.current.add(labelName);
                        label.levNumber = labelValue;
                        newInFrame.push(labelName + ': ' + labelValue);
                        break;
                    case 'S':
                    case 'M':
                    case 'X':
                        if (!scanned.includes('S') && !scanned.includes('M') && !scanned.includes('X')) {
                            scanned.push(labelName);
                            scannedPrefixesRef.current.add(labelName);
                            label.serialNumber = labelValue;
                            newInFrame.push(labelName + ': ' + labelValue);
                        }
                        break;
                    case 'Q':
                        scanned.push(labelName);
                        scannedPrefixesRef.current.add(labelName);
                        label.quantity = labelValue;
                        newInFrame.push(labelName + ': ' + labelValue);
                        break;
                    case 'H':
                        scanned.push(labelName);
                        scannedPrefixesRef.current.add(labelName);
                        label.batchNumber = labelValue;
                        newInFrame.push(labelName + ': ' + labelValue);
                        break;
                }
            }

            if (newInFrame.length > 0) {
                toastBuffer.push(...newInFrame);
                if (toastTimeout) clearTimeout(toastTimeout);
                toastTimeout = setTimeout(flushToast, 500);
            }

            if (newInFrame.length > 0) {
                setScannedLabel(label);
                setScanIntervalAt(moment());

                // All expected fields found — submit immediately
                const allFound = expectedPrefixes.every(p => {
                    if (p === 'S') return scanned.includes('S') || scanned.includes('M') || scanned.includes('X');
                    return scanned.includes(p);
                });
                if (allFound) {
                    finishBatch();
                    return;
                }
            }

            // Overall timeout
            if (now.diff(scanStarted, 's') > (props.order?.ep2_scanner_timeout ? parseInt(props.order.ep2_scanner_timeout) : 15)) {
                finishBatch();
            }
        }, { enableTorch: props.order?.scanning_settings.torchDefault ?? false });

        scanHandleRef.current = handle;
    };

    const stopScan = async () => {
        await scanHandleRef.current?.stop();
        scanHandleRef.current = null;
        restoreWebView();
        const canvas = canvasRef.current;
        if (canvas) canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);

        setScanStartAt(undefined)
        setScanIntervalAt(undefined)
        if (scanTimeout) clearInterval(scanTimeout);
    };

    const scanLabel = async () => {
        let hasPermission = await checkCameraPermission();
        if (!hasPermission) {
            setShowPermissionAlert(true);
        }
        startBatchScan();
    }

    return (
        <Modal
            isOpen={showModal}
            disableClose={false}
            onDidDismiss={() => handleClose()}>

            {showModal && (
                <>
                    <canvas
                        ref={canvasRef}
                        className="scanner-barcode-overlay"
                        width={window.innerWidth}
                        height={window.innerHeight}
                    />
                    <div className="scanner-close-button">
                        <button onClick={() => handleClose()}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>
                    <div className="scanner-torch-button">
                        <button onClick={() => toggleTorch()}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="white">
                                <path d="M6 2h12l-2 6h2l-6 10v-6H8l2-6H6z"/>
                            </svg>
                        </button>
                    </div>
                </>
            )}

            <IonAlert
                isOpen={showPermissionAlert}
                onDidDismiss={() => setShowPermissionAlert(false)}
                header={t('common.permissions.alert')}
                buttons={[
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            setShowPermissionAlert(false);
                        },
                    },
                    {
                        text: t('common.permissions.goToAppSettings'),
                        handler: async () => {
                            setShowPermissionAlert(false);
                            openSettings();
                        }
                    }
                ]}
            />
        </Modal>
    );
};

export default LabelScannerModal;
